import {
    VideoStreamWidgetConfiguration,
    VideoStreamWidgetConfigurationDetails,
    ZodVideoStreamWidgetConfigurationDetails,
} from './types';
import { FadeOutCommand, TimelineSlot, WidgetType } from '../types';
import {
    isIVideoStreamController,
    IVideoStreamController,
    VideoStreamController,
} from './VideoStreamControllers';
import { Log } from '../../AuxillaryService/Log';
import { createVideoStreamController } from './getVideoStreamControllerClass';
import { WidgetWithScheduler } from '../WidgetWithScheduler';
import './videostream.scss';

export class VideoStreamWidget extends WidgetWithScheduler {
    type: WidgetType.VIDEO_STREAM = WidgetType.VIDEO_STREAM;
    declare config: VideoStreamWidgetConfigurationDetails;

    videoStreamController: VideoStreamController | IVideoStreamController;

    constructor(widgetConfiguration: VideoStreamWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);

        this.videoStreamController = createVideoStreamController(
            this.wrapperElement,
            this.config.stream_type,
            this.config.url
                ? {
                      url: this.config.url,
                      aspect_ratio: this.config.aspect_ratio,
                      video_device: this.config.video_device,
                      vlc_options: this.config.vlc_options,
                  }
                : undefined
        );

        //A scheduler action will run that updates this value
        this.hasContentToPlay = false;

        //Check to see if there is video stream content every 15 seconds
        this.schedulerRunInterval = 15;
    }

    protected validateConfigurationDetails(
        maybeVideoStreamWidgetConfigurationDetails: VideoStreamWidgetConfigurationDetails
    ): VideoStreamWidgetConfigurationDetails {
        return ZodVideoStreamWidgetConfigurationDetails.parse(
            maybeVideoStreamWidgetConfigurationDetails
        );
    }

    async schedulerAction(): Promise<void> {
        await this.#checkForContent();
    }

    /**
     * Appends the stream element to the DOM and ensures that the controller has been initialized
     */
    async getReadyForDisplay(): Promise<void> {
        if (isIVideoStreamController(this.videoStreamController)) {
            await this.videoStreamController.getReadyForDisplay();
            await this.#checkForContent();
        } else {
            this.wrapperElement.append(this.videoStreamController.streamElement);

            if (!this.videoStreamController.isInitialized) {
                await this.videoStreamController.initialize();
                await this.#checkForContent();
            }
        }
    }

    /**
     * Determine if the video widget should prepare for transition, or continue to play
     * its content due to override settings or being the next slot as well.
     */
    async beforeFadeOut(
        currentSlot: TimelineSlot,
        nextSlot: TimelineSlot
    ): Promise<Partial<FadeOutCommand> | void> {
        if (
            (this.hasContentToPlay && this.override) ||
            nextSlot.widget.id === currentSlot.widget.id
        ) {
            return {
                cancelFade: true,
                suppressTriggers: true,
            };
        }

        await this.videoStreamController.pause();
    }

    /**
     * Plays the stream.  If that fails, display an error message.
     */
    async play(currentSlot: TimelineSlot): Promise<void> {
        if (!this.hasContentToPlay) {
            Log.Warning(
                `Unable to play ${this.type} video stream ${this.id}, there is no content.`,
                null,
                __fileline,
                this.config
            );

            return;
        }

        await super.play(currentSlot);

        try {
            await this.videoStreamController.play();
        } catch (error) {
            this.hasContentToPlay = false;

            Log.Error(`Video stream failed to play for widget ${this.id}`, null, __fileline, error);
        }
    }

    async _doPause(): Promise<void> {
        await this.videoStreamController.pause();
    }

    /**
     * Stop the stream and remove the stream element from the DOM.
     */
    async stop(): Promise<void> {
        if (isIVideoStreamController(this.videoStreamController)) {
            await this.videoStreamController.stop();
        } else {
            await this.videoStreamController.pause();
            await this.videoStreamController.stop();
            this.videoStreamController.streamElement.remove(); //Save this for later to be reattached
        }
        await super.stop();
    }

    /**
     * Queries the controller to determine if the widget has content to play
     */
    async #checkForContent(): Promise<void> {
        try {
            this.hasContentToPlay = await this.videoStreamController.canBePlayed();
        } catch (error) {
            this.hasContentToPlay = false;
            Log.Warning(
                `VideoStreamWidget ${this.videoStreamController.type} ${this.id} was unable to determine if it has content; defaulting to false.`,
                null,
                __fileline,
                error
            );
        }
    }
}
